import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, ElementRef, OnDestroy } from '@angular/core';
import {
	Component,
	inject,
	input,
	output,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';

import Player from '@vimeo/player';

import { VIDEO_INITIAL_QUALITY } from '@valk-nx/core/lib/core';
import type { VimeoInitialQuality } from '@valk-nx/core/lib/interfaces/video.interface';

/**
 * The shipped types expose their options interface only through a namespace the
 * default import cannot reach, so derive it from the constructor instead.
 */
export type VimeoPlayerOptions = NonNullable<
	ConstructorParameters<typeof Player>[1]
> & {
	initial_quality?: VimeoInitialQuality;
};

@Component({
	selector: 'vp-video',
	template: `<div
		#playerContainer
		aria-label="video player"
		class="size-full [&_iframe]:block [&_iframe]:size-full"
		data-test-class="video-player"
	></div>`,
	host: { class: 'block aspect-video' },
})
export class VideoComponent implements AfterViewInit, OnDestroy {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly defaultInitialQuality = inject(VIDEO_INITIAL_QUALITY, {
		optional: true,
	});

	vimeoId = input.required<number>();
	showControls = input<boolean>(false);
	initialQuality = input<VimeoInitialQuality>();

	playerOptions = input<Partial<Omit<VimeoPlayerOptions, 'id'>>>({});

	/** Emits once the player paints its first frame, so callers can drop a poster. */
	playbackStarted = output<void>();

	@ViewChild('playerContainer', { static: true })
	playerContainer!: ElementRef;

	player!: Player;

	ngAfterViewInit(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			if (!this.vimeoId()) {
				console.error('VimeoPlayerComponent: vimeoId is required');
				return;
			}

			const showControls = this.showControls();
			const initialQuality =
				this.initialQuality() ?? this.defaultInitialQuality;

			const options: VimeoPlayerOptions = {
				id: this.vimeoId(),
				airplay: false,
				autoplay: !showControls,
				byline: false,
				cc: false,
				chromecast: false,
				controls: showControls,
				loop: !showControls,
				muted: !showControls,
				pip: false,
				portrait: false,
				quality_selector: false,
				// Vimeo's responsive embed wraps the iframe in a padded box whose
				// ratio does not always match the video (a 16:9 video came back
				// in a 4:3 box), so the host sizes the iframe as 16:9 itself.
				responsive: false,
				speed: false,
				title: false,
				transcript: false,
				vimeo_logo: false,
				...(initialQuality ? { initial_quality: initialQuality } : {}),
				...this.playerOptions(),
			};

			this.player = new Player(
				this.playerContainer.nativeElement,
				options,
			);
			this.player.on('playing', this.handlePlaying);
		}
	}

	ngOnDestroy(): void {
		if (!this.player) {
			return;
		}

		this.player.off('playing', this.handlePlaying);
		this.player.destroy().catch((error: unknown) => {
			console.warn(
				'VimeoPlayerComponent: failed to destroy the player',
				error,
			);
		});
	}

	private readonly handlePlaying = (): void => {
		this.playbackStarted.emit();
	};
}
